/**
 * Allow runtime toggle via window.__ENABLE_HOTKEYS_DEBUG__ for debugging
 * Default to false to minimize console noise unless explicitly enabled
 */
const ENABLE_LIBRARY_LOGS =
  typeof window !== "undefined" // SSR safety
  && (window as { __ENABLE_HOTKEYS_DEBUG__?: boolean }).__ENABLE_HOTKEYS_DEBUG__ === true;

type LogLevel = "info" | "warn" | "error" | "log";

class DevLogger {
  private enabled: boolean;
  private prefix: string;

  constructor(prefix: string, enabled: boolean) {
    this.prefix = prefix;
    this.enabled = enabled;
  }

  private log(level: LogLevel, ...args: unknown[]): void {
    if (!this.enabled) return;

    const timestamp = new Date().toISOString();
    const message = `[${timestamp}] ${this.prefix}`;

    switch (level) {
      case "info":
        console.info(message, ...args);
        break;
      case "warn":
        console.warn(message, ...args);
        break;
      case "error":
        console.error(message, ...args);
        break;
      case "log":
        console.log(message, ...args);
        break;
      default:
        console.log(message, ...args);
        break;
    }
  }

  info(...args: unknown[]): void {
    this.log("info", ...args);
  }

  warn(...args: unknown[]): void {
    this.log("warn", ...args);
  }

  error(...args: unknown[]): void {
    this.log("error", ...args);
  }

  debug(...args: unknown[]): void {
    this.log("log", ...args);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }
}

// Export for the factory method
export function createLogger(prefix: string, enabled: boolean = ENABLE_LIBRARY_LOGS): DevLogger {
  return new DevLogger(prefix, enabled);
}

// Default library logger utility helper method
export const libraryLogger = createLogger("[hotkeys:library]", ENABLE_LIBRARY_LOGS);

// Export "feature" flag for direct independent checks
export const ENABLE_DEV_LOGS = ENABLE_LIBRARY_LOGS;
